import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import FashionCard from '../components/FashionCard';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BrandDetailPage = () => {
  const { slug } = useParams();
  const [brand, setBrand] = useState(null);
  const [looks, setLooks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const brandName = slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      const [brandRes, looksRes] = await Promise.all([
        axios.get(`${API}/brands/${slug}`),
        axios.get(`${API}/looks`, { params: { brand: brandName } })
      ]);
      setBrand(brandRes.data);
      setLooks(looksRes.data);
    } catch {
      toast.error('Failed to load brand details');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 bg-[#0a0a0a]">
        <div className="h-[60vh] skeleton" />
      </div>
    );
  }

  if (!brand) {
    return (
      <div className="min-h-screen pt-20 bg-[#0a0a0a] flex items-center justify-center">
        <p className="text-gray-500 text-lg">Brand not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="brand-detail-page">
      {/* Hero */}
      <div className="relative h-[60vh] overflow-hidden">
        <img
          src={brand.cover_image}
          alt={brand.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-black/50 to-transparent" />
        
        {/* Back Button */}
        <Link 
          to="/brands" 
          className="absolute top-24 left-6 md:left-12 flex items-center gap-2 text-white/70 hover:text-white transition-colors"
          data-testid="back-to-brands"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm uppercase tracking-wider">All Brands</span>
        </Link>

        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="max-w-7xl mx-auto">
            <span className="text-xs uppercase tracking-[0.2em] text-gray-400 mb-2 block">
              {brand.country} · Est. {brand.founded}
            </span>
            <h1 className="text-5xl md:text-7xl font-serif italic text-white mb-4" data-testid="brand-name">
              {brand.name}
            </h1>
            <p className="text-gray-300 text-lg max-w-2xl leading-relaxed">
              {brand.description}
            </p>
          </div>
        </div>
      </div>

      {/* Community Looks */}
      <div className="py-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#C41E3A] mb-4">Community</p>
              <h2 className="text-3xl md:text-4xl font-serif italic text-white">
                {brand.name} Looks
              </h2>
            </div>
          </div>

          {looks.length > 0 ? (
            <div className="bento-grid">
              {looks.map((look, index) => (
                <FashionCard key={look.id} look={look} featured={index === 0} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 border border-white/5 bg-[#121212]">
              <p className="text-gray-500 mb-6">No looks featuring {brand.name} yet</p>
              <Link to="/create">
                <Button className="btn-luxury btn-luxury-accent">
                  Be the first to share
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrandDetailPage;
